import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

export type VisibleFieldKey =
  | 'title'
  | 'subtitle'
  | 'description'
  | 'shopContactMail'
  | 'infoTitle'
  | 'infoDescription'
  | 'redirectUrl'
  | 'urlName'
  | 'infoImageUrl'
  | 'infoVideoUrl'
  | 'itemQuantity';

export interface ComponentInfoFormValue {
  infoId: number;
  infoTitle: string;
  infoDescription: string;
  redirectUrl: string;
  urlName: string;
  imageURL: string;
  blob: { file: File; key: string } | null;
  videoURL: string;
  videoBlob: { file: File; key: string } | null;
  videoManualURL: string;
  videoUrlType: number;
  previousVideoUrl: string;
  previousManualUrl: string;
  isYouTube: boolean;
}

export interface SerializedComponentPayload {
  descriptions: string;
  redirectUrls: string;
  infoTitles: string;
  urlNames: string;
  imageUrls: string;
  videoUrls: string;
  videoUrlTypes: string;
}

export interface ComponentTranslationInfoValue {
  infoTitle: string;
  infoDescription: string;
  redirectUrl: string;
  urlName: string;
}

export interface ComponentTranslationValue {
  manual: boolean;
  title: string;
  subtitle: string;
  description: string;
  infoFields: ComponentTranslationInfoValue[];
}

export interface ResolvedEnglishTranslation {
  value: ComponentTranslationValue;
  hasExistingTranslation: boolean;
  isManual: boolean;
}

export interface ReviewSettingsValue {
  componentId: number;
  shopId: number;
  reviewAuthMode: number;
  moderationMode: number;
  allowGuestReviews: boolean;
  allowReviewTitle: boolean;
  requireReviewTitle: boolean;
  showAverageRating: boolean;
  showReviewCount: boolean;
  showApprovedReviews: boolean;
  minRating: number;
  maxRating: number;
  maxCommentLength: number;
}

export type ReviewsDisplayMode = 'carousel' | 'grid' | 'list';

export interface ReviewsDisplayConfigValue {
  schema: 'reviews-display';
  version: 1;
  showAverageRating: boolean;
  showReviewCount: boolean;
  showStars: boolean;
  showReviewTitle: boolean;
  showReviewerName: boolean;
  showReviewDate: boolean;
  displayMode: ReviewsDisplayMode;
  maxVisibleReviews: number;
}

export const REVIEW_COMPONENT_TYPE_ID = 29;
export const REVIEWS_DISPLAY_COMPONENT_TYPE_ID = 30;
export const REVIEWS_DISPLAY_SCHEMA = 'reviews-display';
export const REVIEWS_DISPLAY_DISPLAY_MODES: ReviewsDisplayMode[] = ['carousel', 'grid', 'list'];
export const REVIEWS_DISPLAY_DEFAULT_DISPLAY_MODE: ReviewsDisplayMode = 'carousel';
export const REVIEWS_DISPLAY_MAX_VISIBLE_DEFAULT = 6;
export const REVIEWS_DISPLAY_MAX_VISIBLE_MIN = 1;
export const REVIEWS_DISPLAY_MAX_VISIBLE_MAX = 20;
export const REVIEW_AUTH_LOGGED_ONLY = 1;
export const REVIEW_AUTH_LOGGED_AND_GUEST = 2;
export const REVIEW_MODERATION_REQUIRED = 1;
export const REVIEW_MODERATION_AUTO = 2;
export const REVIEW_MIN_RATING = 1;
export const REVIEW_MAX_RATING = 5;
export const REVIEW_MAX_COMMENT_LENGTH_DEFAULT = 1000;
export const REVIEW_MAX_COMMENT_LENGTH_MIN = 100;
export const REVIEW_MAX_COMMENT_LENGTH_MAX = 2500;

export const COMPONENT_FIELD_VISIBILITY_CONFIG: Record<number, VisibleFieldKey[]> = {
  1: ['infoImageUrl', 'infoTitle', 'infoDescription', 'redirectUrl', 'urlName'],
  2: ['infoImageUrl', 'infoDescription', 'redirectUrl'],
  3: ['title', 'infoImageUrl', 'infoTitle', 'infoDescription', 'redirectUrl', 'urlName'],
  4: ['title', 'description', 'infoImageUrl', 'infoTitle', 'infoDescription', 'redirectUrl'],
  5: ['title', 'description', 'infoImageUrl', 'infoTitle', 'infoDescription', 'redirectUrl'],
  6: ['title', 'description', 'infoTitle', 'infoDescription', 'redirectUrl'],
  7: ['infoImageUrl', 'title', 'description', 'infoTitle', 'infoDescription', 'redirectUrl', 'urlName'],
  8: ['title', 'subtitle', 'itemQuantity'],
  9: ['title', 'subtitle', 'itemQuantity'],
  10: ['title', 'subtitle', 'itemQuantity'],
  11: ['infoImageUrl', 'title', 'description', 'infoTitle', 'infoDescription', 'redirectUrl', 'urlName'],
  12: ['infoImageUrl', 'redirectUrl'],
  13: ['title', 'subtitle', 'description', 'redirectUrl'],
  14: ['title', 'subtitle', 'description', 'infoTitle', 'infoDescription', 'redirectUrl', 'urlName', 'infoImageUrl'],
  15: ['infoImageUrl', 'redirectUrl', 'urlName'],
  16: ['infoImageUrl', 'redirectUrl'],
  17: ['infoVideoUrl', 'redirectUrl', 'infoTitle', 'infoDescription'],
  18: ['infoVideoUrl', 'infoTitle', 'infoDescription', 'redirectUrl', 'urlName'],
  19: ['title', 'infoImageUrl', 'redirectUrl'],
  20: ['title', 'subtitle', 'infoImageUrl', 'redirectUrl'],
  21: ['title', 'subtitle', 'infoImageUrl', 'redirectUrl'],
  22: ['infoVideoUrl', 'redirectUrl'],
  23: ['title', 'infoImageUrl', 'infoTitle', 'infoDescription', 'redirectUrl', 'urlName'],
  24: ['title', 'description', 'shopContactMail'],
  25: ['title', 'description', 'infoImageUrl', 'infoTitle', 'infoDescription', 'redirectUrl'],
  26: ['title', 'description', 'infoImageUrl', 'infoTitle', 'infoDescription', 'redirectUrl'],
  27: ['title', 'description', 'infoImageUrl', 'infoTitle', 'infoDescription', 'redirectUrl'],
  [REVIEW_COMPONENT_TYPE_ID]: ['title', 'description'],
  [REVIEWS_DISPLAY_COMPONENT_TYPE_ID]: ['title', 'description'],
};

const COMPONENT_DYNAMIC_FIELDS: VisibleFieldKey[] = [
  'title',
  'subtitle',
  'description',
  'shopContactMail',
  'itemQuantity',
];

export function buildVisibleFields(componentTypeId: number): Set<VisibleFieldKey> {
  return new Set(COMPONENT_FIELD_VISIBILITY_CONFIG[componentTypeId] || []);
}

export function createComponentForm(fb: FormBuilder): FormGroup {
  return fb.group({
    component: fb.group({
      componentId: [0],
      componentName: ['', Validators.required],
      title: [''],
      subtitle: [''],
      description: [''],
      shopContactMail: [''],
      componentTypeId: [0, Validators.required],
      status: [0],
      position: [0],
      itemQuantity: [0],
      itemListId: [0],
      itemSort: [0],
      categoryId: [0],
      subCategoryId: [0],
      subSubCategoryId: [0],
      collectionId: [0],
      pageId: [0],
    }),
    infoFields: fb.array([]),
    reviewSettings: createReviewSettingsGroup(fb),
    reviewsDisplayConfig: createReviewsDisplayConfigGroup(fb),
  });
}

export function createReviewSettingsGroup(fb: FormBuilder, seed: Partial<ReviewSettingsValue> = {}): FormGroup {
  const defaults = createDefaultReviewSettings(Number(seed.shopId || 0), Number(seed.componentId || 0));
  const value = normalizeReviewSettingsFromApi({ ...defaults, ...seed }, defaults.shopId, defaults.componentId);

  return fb.group({
    componentId: [value.componentId],
    shopId: [value.shopId],
    reviewAuthMode: [value.reviewAuthMode, Validators.required],
    moderationMode: [value.moderationMode, Validators.required],
    allowGuestReviews: [value.allowGuestReviews],
    allowReviewTitle: [value.allowReviewTitle],
    requireReviewTitle: [value.requireReviewTitle],
    showAverageRating: [value.showAverageRating],
    showReviewCount: [value.showReviewCount],
    showApprovedReviews: [value.showApprovedReviews],
    minRating: [REVIEW_MIN_RATING],
    maxRating: [REVIEW_MAX_RATING],
    maxCommentLength: [
      value.maxCommentLength,
      [Validators.required, Validators.min(REVIEW_MAX_COMMENT_LENGTH_MIN), Validators.max(REVIEW_MAX_COMMENT_LENGTH_MAX)],
    ],
  });
}

export function createReviewsDisplayConfigGroup(fb: FormBuilder, seed: Partial<ReviewsDisplayConfigValue> = {}): FormGroup {
  const value = normalizeReviewsDisplayConfig(seed);

  return fb.group({
    schema: [value.schema],
    version: [value.version],
    showAverageRating: [value.showAverageRating],
    showReviewCount: [value.showReviewCount],
    showStars: [value.showStars],
    showReviewTitle: [value.showReviewTitle],
    showReviewerName: [value.showReviewerName],
    showReviewDate: [value.showReviewDate],
    displayMode: [value.displayMode, Validators.required],
    maxVisibleReviews: [
      value.maxVisibleReviews,
      [
        Validators.required,
        Validators.min(REVIEWS_DISPLAY_MAX_VISIBLE_MIN),
        Validators.max(REVIEWS_DISPLAY_MAX_VISIBLE_MAX),
      ],
    ],
  });
}

export function createInfoFieldGroup(
  fb: FormBuilder,
  visibleFields: Set<VisibleFieldKey>,
  seed: Partial<ComponentInfoFormValue> = {}
): FormGroup {
  const group = fb.group({
    infoId: [seed.infoId ?? 0],
    infoTitle: [seed.infoTitle ?? ''],
    infoDescription: [seed.infoDescription ?? ''],
    redirectUrl: [seed.redirectUrl ?? '', [optionalHttpUrlValidator()]],
    urlName: [seed.urlName ?? ''],
    imageURL: [seed.imageURL ?? ''],
    blob: [seed.blob ?? null],
    videoURL: [seed.videoURL ?? ''],
    videoBlob: [seed.videoBlob ?? null],
    videoManualURL: [seed.videoManualURL ?? ''],
    videoUrlType: [Number(seed.videoUrlType ?? (visibleFields.has('infoVideoUrl') ? 1 : 0))],
    previousVideoUrl: [seed.previousVideoUrl ?? ''],
    previousManualUrl: [seed.previousManualUrl ?? ''],
    isYouTube: [seed.isYouTube ?? false],
  });

  applyInfoFieldValidators(group, visibleFields);
  return group;
}

export function applyDynamicValidators(
  componentGroup: FormGroup,
  infoFields: FormArray,
  visibleFields: Set<VisibleFieldKey>
): void {
  COMPONENT_DYNAMIC_FIELDS.forEach((field) => {
    const control = componentGroup.get(field);
    if (!control) {
      return;
    }

    control.clearValidators();

    if (visibleFields.has(field)) {
      if (field === 'shopContactMail') {
        control.setValidators([Validators.required, Validators.email]);
      } else if (field === 'itemQuantity') {
        control.setValidators([Validators.required, Validators.min(1)]);
      } else {
        control.setValidators([richTextRequiredValidator()]);
      }
    }

    control.updateValueAndValidity({ emitEvent: false });
  });

  infoFields.controls.forEach((control) => {
    applyInfoFieldValidators(control as FormGroup, visibleFields);
  });
}

export function applyInfoFieldValidators(group: FormGroup, visibleFields: Set<VisibleFieldKey>): void {
  setControlValidators(group, 'infoTitle', visibleFields.has('infoTitle') ? [richTextRequiredValidator()] : []);
  setControlValidators(group, 'infoDescription', visibleFields.has('infoDescription') ? [richTextRequiredValidator()] : []);
  setControlValidators(group, 'urlName', visibleFields.has('urlName') ? [Validators.required] : []);
  setControlValidators(group, 'imageURL', visibleFields.has('infoImageUrl') ? [Validators.required] : []);
  setControlValidators(group, 'redirectUrl', [optionalHttpUrlValidator()]);
}

export function serializeInfoFields(infoFields: ComponentInfoFormValue[]): SerializedComponentPayload {
  return {
    descriptions: infoFields.map((field) => sanitizeString(field.infoDescription)).join('|'),
    redirectUrls: infoFields.map((field) => sanitizeString(field.redirectUrl)).join('|'),
    infoTitles: infoFields.map((field) => sanitizeString(field.infoTitle)).join('|'),
    urlNames: infoFields.map((field) => sanitizeString(field.urlName)).join('|'),
    imageUrls: infoFields.map((field) => field.imageURL || '').join('|'),
    videoUrls: infoFields
      .map((field) => (Number(field.videoUrlType) === 2 ? field.videoManualURL || field.videoURL || '' : field.videoURL || ''))
      .join('|'),
    videoUrlTypes: infoFields.map((field) => String(field.videoUrlType ?? 0)).join('|'),
  };
}

export function createEmptyTranslatedInfoField(): ComponentTranslationInfoValue {
  return {
    infoTitle: '',
    infoDescription: '',
    redirectUrl: '',
    urlName: '',
  };
}

export function createEmptyTranslatedState(infoFieldCount: number = 0): ComponentTranslationValue {
  return {
    manual: false,
    title: '',
    subtitle: '',
    description: '',
    infoFields: Array.from({ length: infoFieldCount }, () => createEmptyTranslatedInfoField()),
  };
}

export function cloneTranslatedState(value: ComponentTranslationValue): ComponentTranslationValue {
  return {
    manual: value.manual === true,
    title: sanitizeString(value.title),
    subtitle: sanitizeString(value.subtitle),
    description: sanitizeString(value.description),
    infoFields: value.infoFields.map((field) => ({
      infoTitle: sanitizeString(field.infoTitle),
      infoDescription: sanitizeString(field.infoDescription),
      redirectUrl: sanitizeString(field.redirectUrl),
      urlName: sanitizeString(field.urlName),
    })),
  };
}

export function buildLocalTranslatedState(component: any, infoFields: ComponentInfoFormValue[]): ComponentTranslationValue {
  return {
    manual: false,
    title: sanitizeString(component?.title),
    subtitle: sanitizeString(component?.subtitle),
    description: sanitizeString(component?.description),
    infoFields: infoFields.map((field) => ({
      infoTitle: sanitizeString(field.infoTitle),
      infoDescription: sanitizeString(field.infoDescription),
      redirectUrl: sanitizeString(field.redirectUrl),
      urlName: sanitizeString(field.urlName),
    })),
  };
}

export function ensureTranslatedInfoFieldCount(
  value: ComponentTranslationValue,
  fallbackInfoFields: ComponentTranslationInfoValue[]
): ComponentTranslationValue {
  return {
    manual: value.manual === true,
    title: sanitizeString(value.title),
    subtitle: sanitizeString(value.subtitle),
    description: sanitizeString(value.description),
    infoFields: fallbackInfoFields.map((fallbackField, index) => ({
      infoTitle: sanitizeString(value.infoFields[index]?.infoTitle ?? fallbackField.infoTitle),
      infoDescription: sanitizeString(value.infoFields[index]?.infoDescription ?? fallbackField.infoDescription),
      redirectUrl: sanitizeString(value.infoFields[index]?.redirectUrl ?? fallbackField.redirectUrl),
      urlName: sanitizeString(value.infoFields[index]?.urlName ?? fallbackField.urlName),
    })),
  };
}

export function buildEnglishTranslationState(
  rawTranslatedContent: unknown,
  component: any,
  infoFields: ComponentInfoFormValue[]
): ResolvedEnglishTranslation {
  const fallback = buildLocalTranslatedState(component, infoFields);
  const parsed = parseTranslatedContent(rawTranslatedContent);
  const rawEnglish = parsed?.en;

  if (!rawEnglish || typeof rawEnglish !== 'object') {
    return {
      value: fallback,
      hasExistingTranslation: false,
      isManual: false,
    };
  }

  const rawEnglishInfoFields = Array.isArray(rawEnglish.infoFields) ? rawEnglish.infoFields : [];
  const translatedInfoById = new Map<number, any>();

  rawEnglishInfoFields.forEach((field: any) => {
    const translatedInfoId = Number(field?.infoId || 0);
    if (translatedInfoId > 0 && !translatedInfoById.has(translatedInfoId)) {
      translatedInfoById.set(translatedInfoId, field);
    }
  });

  const normalizedInfoFields = fallback.infoFields.map((fallbackField, index) => {
    const currentInfoId = Number(infoFields[index]?.infoId || 0);
    const matchedById = currentInfoId > 0 ? translatedInfoById.get(currentInfoId) : null;
    const matchedByIndex = rawEnglishInfoFields[index];
    const matchedField = matchedById || matchedByIndex || null;

    return {
      infoTitle: sanitizeString(matchedField?.infoTitle ?? fallbackField.infoTitle),
      infoDescription: sanitizeString(matchedField?.infoDescription ?? fallbackField.infoDescription),
      redirectUrl: sanitizeString(matchedField?.redirectUrl ?? fallbackField.redirectUrl),
      urlName: sanitizeString(matchedField?.urlName ?? fallbackField.urlName),
    };
  });

  const normalized = ensureTranslatedInfoFieldCount(
    {
      manual: rawEnglish.manual === true,
      title: sanitizeString(rawEnglish.title ?? fallback.title),
      subtitle: sanitizeString(rawEnglish.subtitle ?? fallback.subtitle),
      description: sanitizeString(rawEnglish.description ?? fallback.description),
      infoFields: normalizedInfoFields,
    },
    fallback.infoFields
  );

  return {
    value: normalized,
    hasExistingTranslation: hasTranslatedValueContent(normalized),
    isManual: normalized.manual === true,
  };
}

export function buildManualTranslatedPayload(value: ComponentTranslationValue): { en: ComponentTranslationValue } {
  const normalized = cloneTranslatedState(value);
  normalized.manual = true;
  return {
    en: normalized,
  };
}

export function shouldSendManualTranslatedContent(
  currentValue: ComponentTranslationValue,
  initialValue: ComponentTranslationValue,
  manualEnabled: boolean,
  persistedManual: boolean
): boolean {
  if (!manualEnabled) {
    return false;
  }

  if (persistedManual) {
    return true;
  }

  return !areTranslatedStatesEqual(currentValue, initialValue);
}

export function hasTranslatedValueContent(value: ComponentTranslationValue): boolean {
  if (hasMeaningfulText(value.title) || hasMeaningfulText(value.subtitle) || hasMeaningfulText(value.description)) {
    return true;
  }

  return value.infoFields.some((field) => {
    return (
      hasMeaningfulText(field.infoTitle) ||
      hasMeaningfulText(field.infoDescription) ||
      sanitizeString(field.redirectUrl).length > 0 ||
      sanitizeString(field.urlName).length > 0
    );
  });
}

export function sanitizeString(str: any): string {
  if (!str) {
    return '';
  }

  return String(str).trim();
}

export function isReviewComponentType(
  componentTypeId: number,
  componentTypes: any[] = [],
  componentTypeName: string = ''
): boolean {
  const numericId = Number(componentTypeId || 0);

  if (isReviewsDisplayComponentType(numericId, componentTypes, componentTypeName)) {
    return false;
  }

  const matchedType = Array.isArray(componentTypes)
    ? componentTypes.find((type) => Number(type?.componentTypeId || type?.typeId || 0) === numericId)
    : null;

  if (matchedType) {
    return isReviewComponentTypeName(matchedType.componentTypeName || matchedType.name || '');
  }

  if (componentTypeName && isReviewComponentTypeName(componentTypeName)) {
    return true;
  }

  return numericId === REVIEW_COMPONENT_TYPE_ID;
}

export function isReviewsDisplayComponentType(
  componentTypeId: number,
  componentTypes: any[] = [],
  componentTypeName: string = ''
): boolean {
  const numericId = Number(componentTypeId || 0);

  if (numericId === REVIEWS_DISPLAY_COMPONENT_TYPE_ID) {
    return true;
  }

  if (componentTypeName && isReviewsDisplayComponentTypeName(componentTypeName)) {
    return true;
  }

  const matchedType = Array.isArray(componentTypes)
    ? componentTypes.find((type) => Number(type?.componentTypeId || type?.typeId || 0) === numericId)
    : null;

  return isReviewsDisplayComponentTypeName(matchedType?.componentTypeName || matchedType?.name || '');
}

export function createDefaultReviewSettings(shopId: number, componentId: number = 0): ReviewSettingsValue {
  return {
    componentId: Number(componentId || 0),
    shopId: Number(shopId || 0),
    reviewAuthMode: REVIEW_AUTH_LOGGED_ONLY,
    moderationMode: REVIEW_MODERATION_REQUIRED,
    allowGuestReviews: false,
    allowReviewTitle: false,
    requireReviewTitle: false,
    showAverageRating: true,
    showReviewCount: true,
    showApprovedReviews: true,
    minRating: REVIEW_MIN_RATING,
    maxRating: REVIEW_MAX_RATING,
    maxCommentLength: REVIEW_MAX_COMMENT_LENGTH_DEFAULT,
  };
}

export function normalizeReviewSettingsFromApi(
  data: any,
  shopId: number,
  componentId: number = 0
): ReviewSettingsValue {
  const defaults = createDefaultReviewSettings(shopId, componentId);
  const raw = data?.settings || data?.reviewSettings || data || {};
  const reviewAuthMode = normalizeReviewAuthMode(raw, defaults.reviewAuthMode);
  const allowReviewTitle = normalizeBoolean(raw.allowReviewTitle, defaults.allowReviewTitle);

  return {
    componentId: Number(raw.componentId || defaults.componentId),
    shopId: Number(raw.shopId || defaults.shopId),
    reviewAuthMode,
    moderationMode: normalizeReviewMode(raw.moderationMode, defaults.moderationMode, [
      REVIEW_MODERATION_REQUIRED,
      REVIEW_MODERATION_AUTO,
    ]),
    allowGuestReviews: reviewAuthMode === REVIEW_AUTH_LOGGED_AND_GUEST,
    allowReviewTitle,
    requireReviewTitle: allowReviewTitle ? normalizeBoolean(raw.requireReviewTitle, defaults.requireReviewTitle) : false,
    showAverageRating: normalizeBoolean(raw.showAverageRating, defaults.showAverageRating),
    showReviewCount: normalizeBoolean(raw.showReviewCount, defaults.showReviewCount),
    showApprovedReviews: normalizeBoolean(raw.showApprovedReviews, defaults.showApprovedReviews),
    minRating: REVIEW_MIN_RATING,
    maxRating: REVIEW_MAX_RATING,
    maxCommentLength: normalizeBoundedNumber(
      raw.maxCommentLength,
      defaults.maxCommentLength,
      REVIEW_MAX_COMMENT_LENGTH_MIN,
      REVIEW_MAX_COMMENT_LENGTH_MAX
    ),
  };
}

export function buildReviewSettingsPayload(formValue: any): ReviewSettingsValue {
  const normalized = normalizeReviewSettingsFromApi(formValue, Number(formValue?.shopId || 0), Number(formValue?.componentId || 0));

  return {
    ...normalized,
    allowGuestReviews: normalized.reviewAuthMode === REVIEW_AUTH_LOGGED_AND_GUEST,
    requireReviewTitle: normalized.allowReviewTitle && normalized.requireReviewTitle,
    minRating: REVIEW_MIN_RATING,
    maxRating: REVIEW_MAX_RATING,
  };
}

export function createDefaultReviewsDisplayConfig(): ReviewsDisplayConfigValue {
  return {
    schema: REVIEWS_DISPLAY_SCHEMA,
    version: 1,
    showAverageRating: true,
    showReviewCount: true,
    showStars: true,
    showReviewTitle: true,
    showReviewerName: true,
    showReviewDate: true,
    displayMode: REVIEWS_DISPLAY_DEFAULT_DISPLAY_MODE,
    maxVisibleReviews: REVIEWS_DISPLAY_MAX_VISIBLE_DEFAULT,
  };
}

export function normalizeReviewsDisplayConfig(rawConfig: any): ReviewsDisplayConfigValue {
  const defaults = createDefaultReviewsDisplayConfig();
  const parsed = parseReviewsDisplayConfig(rawConfig);

  return {
    schema: REVIEWS_DISPLAY_SCHEMA,
    version: 1,
    showAverageRating: normalizeBoolean(parsed?.showAverageRating, defaults.showAverageRating),
    showReviewCount: normalizeBoolean(parsed?.showReviewCount, defaults.showReviewCount),
    showStars: normalizeBoolean(parsed?.showStars, defaults.showStars),
    showReviewTitle: normalizeBoolean(parsed?.showReviewTitle, defaults.showReviewTitle),
    showReviewerName: normalizeBoolean(parsed?.showReviewerName, defaults.showReviewerName),
    showReviewDate: normalizeBoolean(parsed?.showReviewDate, defaults.showReviewDate),
    displayMode: normalizeReviewsDisplayMode(parsed?.displayMode),
    maxVisibleReviews: normalizeBoundedNumber(
      parsed?.maxVisibleReviews,
      defaults.maxVisibleReviews,
      REVIEWS_DISPLAY_MAX_VISIBLE_MIN,
      REVIEWS_DISPLAY_MAX_VISIBLE_MAX
    ),
  };
}

export function buildReviewsDisplayConfigPayload(formValue: any): ReviewsDisplayConfigValue {
  return normalizeReviewsDisplayConfig(formValue);
}

export function isOptionalHttpUrlValid(value: string | null | undefined): boolean {
  const normalizedValue = sanitizeString(value);
  if (!normalizedValue) {
    return true;
  }

  return /^https?:\/\/.+/i.test(normalizedValue);
}

export function optionalHttpUrlValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return isOptionalHttpUrlValid(control.value) ? null : { invalidUrl: true };
  };
}

export function richTextRequiredValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return hasMeaningfulText(control.value) ? null : { required: true };
  };
}

export function hasMeaningfulText(value: string | null | undefined): boolean {
  if (value == null) {
    return false;
  }

  const normalized = String(value)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return normalized.length > 0;
}

export function isYouTubeUrl(url: string): boolean {
  return /youtube\.com|youtu\.be/i.test(url || '');
}

export function mapApiInfoToFormSeed(info: any): Partial<ComponentInfoFormValue> {
  const rawVideoUrl = info?.infoVideoUrl || '';
  const numericVideoType = Number(info?.videoUrlType || (rawVideoUrl ? 1 : 0));
  const manualUrl = numericVideoType === 2 ? info?.videoManualURL || rawVideoUrl || '' : '';
  const uploadedVideoUrl = numericVideoType === 1 ? rawVideoUrl : '';

  return {
    infoId: Number(info?.infoId || 0),
    infoTitle: info?.infoTitle || '',
    infoDescription: info?.infoDescription || '',
    redirectUrl: info?.redirectUrl || '',
    urlName: info?.urlName || '',
    imageURL: info?.infoImageUrl || '',
    blob: null,
    videoURL: uploadedVideoUrl,
    videoBlob: null,
    videoManualURL: manualUrl,
    videoUrlType: numericVideoType,
    previousVideoUrl: uploadedVideoUrl,
    previousManualUrl: manualUrl,
    isYouTube: isYouTubeUrl(manualUrl || rawVideoUrl),
  };
}

function parseTranslatedContent(rawTranslatedContent: unknown): any | null {
  if (!rawTranslatedContent) {
    return null;
  }

  if (typeof rawTranslatedContent === 'string') {
    try {
      return JSON.parse(rawTranslatedContent);
    } catch (error) {
      console.error('Invalid translatedContent JSON payload:', error);
      return null;
    }
  }

  return typeof rawTranslatedContent === 'object' ? rawTranslatedContent : null;
}

function areTranslatedStatesEqual(left: ComponentTranslationValue, right: ComponentTranslationValue): boolean {
  const normalizedLeft = cloneTranslatedState(left);
  const normalizedRight = cloneTranslatedState(right);

  normalizedLeft.manual = false;
  normalizedRight.manual = false;

  return JSON.stringify(normalizedLeft) === JSON.stringify(normalizedRight);
}

function setControlValidators(controlGroup: FormGroup, controlName: string, validators: ValidatorFn[]): void {
  const control = controlGroup.get(controlName);
  if (!control) {
    return;
  }

  control.clearValidators();
  control.setValidators(validators);
  control.updateValueAndValidity({ emitEvent: false });
}

function isReviewComponentTypeName(name: string): boolean {
  const normalized = sanitizeString(name)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  return normalized === 'reviews' || normalized === 'resenas' || normalized.includes('review') || normalized.includes('resena');
}

function isReviewsDisplayComponentTypeName(name: string): boolean {
  const normalized = sanitizeString(name)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  return (
    normalized.includes('reviews display') ||
    normalized.includes('review display') ||
    normalized.includes('listado de resenas') ||
    normalized.includes('listado resenas') ||
    normalized.includes('resenas display')
  );
}

function parseReviewsDisplayConfig(rawConfig: any): any {
  if (!rawConfig) {
    return {};
  }

  if (typeof rawConfig === 'string') {
    try {
      return JSON.parse(rawConfig);
    } catch (error) {
      console.error('Invalid reviews display componentConfig payload:', error);
      return {};
    }
  }

  return typeof rawConfig === 'object' ? rawConfig : {};
}

function normalizeReviewsDisplayMode(value: any): ReviewsDisplayMode {
  const normalized = sanitizeString(value).toLowerCase();
  return REVIEWS_DISPLAY_DISPLAY_MODES.includes(normalized as ReviewsDisplayMode)
    ? (normalized as ReviewsDisplayMode)
    : REVIEWS_DISPLAY_DEFAULT_DISPLAY_MODE;
}

function normalizeBoolean(value: any, fallback: boolean): boolean {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  const normalized = String(value).trim().toLowerCase();
  if (['1', 'true', 'yes', 'si', 'sí'].includes(normalized)) {
    return true;
  }

  if (['0', 'false', 'no'].includes(normalized)) {
    return false;
  }

  return fallback;
}

function normalizeReviewMode(value: any, fallback: number, allowedValues: number[]): number {
  const numericValue = Number(value);
  return allowedValues.includes(numericValue) ? numericValue : fallback;
}

function normalizeReviewAuthMode(raw: any, fallback: number): number {
  const authMode = normalizeReviewMode(raw?.reviewAuthMode, 0, [
    REVIEW_AUTH_LOGGED_ONLY,
    REVIEW_AUTH_LOGGED_AND_GUEST,
  ]);

  if (authMode) {
    return authMode;
  }

  return normalizeBoolean(raw?.allowGuestReviews, fallback === REVIEW_AUTH_LOGGED_AND_GUEST)
    ? REVIEW_AUTH_LOGGED_AND_GUEST
    : REVIEW_AUTH_LOGGED_ONLY;
}

function normalizeBoundedNumber(value: any, fallback: number, min: number, max: number): number {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return fallback;
  }

  return Math.min(Math.max(numericValue, min), max);
}
